/**
 * Proprietary wallet provider detection — zero WalletConnect, zero RainbowKit.
 * Direct injected EIP-1193 detection for EVM wallets and Stellar extensions.
 *
 * Multi-provider conflict: when MetaMask + Coinbase are both installed, one overrides
 * window.ethereum but both appear in window.ethereum.providers[]. We iterate that array.
 */
import { getAddress } from 'ethers'
import type { EthereumProvider } from './walletDetect'

// ─── Provider IDs ─────────────────────────────────────────────────────────────

export type ProviderId =
  | 'metamask'
  | 'coinbase'
  | 'rabby'
  | 'brave'
  | 'generic-evm'
  | 'freighter'
  | 'lobstr'

// ─── Interface ────────────────────────────────────────────────────────────────

export interface WalletProvider {
  id: ProviderId
  name: string
  type: 'evm' | 'stellar'
  /** Emoji or icon identifier shown in WalletModal */
  icon: string
  installUrl?: string
  isDetected: boolean
  /**
   * Triggers the wallet connection popup.
   * Returns checksummed EVM address (EIP-55) or Stellar public key (G…).
   */
  connect(): Promise<string>
  /** Raw EIP-1193 provider for EVM wallets — null for Stellar. */
  getEIP1193Provider(): EthereumProvider | null
}

// ─── Internal extended provider type ─────────────────────────────────────────

interface EvmProvider extends EthereumProvider {
  isCoinbaseWallet?: boolean
  isBraveWallet?: boolean
}

// ─── Multi-provider list ──────────────────────────────────────────────────────

/**
 * Returns the flat list of injected EVM providers.
 * When MetaMask + Coinbase coexist, they inject a `.providers` array on window.ethereum.
 */
function getEvmProviderList(): EvmProvider[] {
  if (typeof window === 'undefined' || !window.ethereum) return []
  const eth = window.ethereum as EvmProvider
  if (eth.providers && eth.providers.length > 0) {
    return eth.providers as EvmProvider[]
  }
  return [eth]
}

function findEvmProvider(predicate: (p: EvmProvider) => boolean): EvmProvider | null {
  return getEvmProviderList().find(predicate) ?? null
}

// ─── Detection helpers ────────────────────────────────────────────────────────

function detectMetaMask(): EvmProvider | null {
  // Exclude Coinbase and Brave — both set isMetaMask to true for compat reasons
  return findEvmProvider((p) => !!p.isMetaMask && !p.isCoinbaseWallet && !p.isBraveWallet)
}

function detectCoinbase(): EvmProvider | null {
  const fromProviders = findEvmProvider((p) => !!p.isCoinbaseWallet)
  if (fromProviders) return fromProviders
  // Coinbase Wallet SDK injects window.coinbaseWalletExtension when window.ethereum is taken
  if (typeof window !== 'undefined' && window.coinbaseWalletExtension) {
    return window.coinbaseWalletExtension as unknown as EvmProvider
  }
  return null
}

function detectRabby(): EvmProvider | null {
  return findEvmProvider((p) => !!p.isRabby)
}

function detectBrave(): EvmProvider | null {
  return findEvmProvider((p) => !!p.isBraveWallet)
}

function detectGenericEvm(): EvmProvider | null {
  const eth =
    typeof window !== 'undefined' ? (window.ethereum as EvmProvider | undefined) : undefined
  if (!eth) return null
  // Only expose generic fallback if it doesn't match any named wallet
  if (eth.isMetaMask || eth.isCoinbaseWallet || eth.isBraveWallet || eth.isRabby) return null
  return eth
}

// ─── EVM connection helper ────────────────────────────────────────────────────

async function evmConnect(rawProvider: EvmProvider): Promise<string> {
  const accounts = (await rawProvider.request({
    method: 'eth_requestAccounts',
  })) as string[]
  if (!accounts.length) throw new Error('No accounts returned from wallet')
  return getAddress(accounts[0]) // EIP-55 checksum
}

// ─── EVM provider factory ─────────────────────────────────────────────────────

function makeEvmProvider(
  id: ProviderId,
  name: string,
  icon: string,
  installUrl: string | undefined,
  rawProvider: EvmProvider | null
): WalletProvider {
  return {
    id,
    name,
    type: 'evm',
    icon,
    installUrl,
    isDetected: !!rawProvider,
    connect: async () => {
      if (!rawProvider) throw new Error(`${name} is not installed`)
      return evmConnect(rawProvider)
    },
    getEIP1193Provider: () => rawProvider,
  }
}

// ─── Stellar providers ────────────────────────────────────────────────────────

function makeFreighter(): WalletProvider {
  const detected = typeof window !== 'undefined' && !!window.freighter
  return {
    id: 'freighter',
    name: 'Freighter',
    type: 'stellar',
    icon: '⭐',
    installUrl: 'https://freighter.app',
    isDetected: detected,
    connect: async () => {
      if (!window.freighter) throw new Error('Freighter is not installed')
      const connected = await window.freighter.isConnected()
      if (!connected) throw new Error('Freighter is not connected. Open the extension and unlock it.')
      return window.freighter.getPublicKey()
    },
    getEIP1193Provider: () => null,
  }
}

function makeLobstr(): WalletProvider {
  const detected = typeof window !== 'undefined' && !!window.lobstr
  return {
    id: 'lobstr',
    name: 'LOBSTR',
    type: 'stellar',
    icon: '🦞',
    installUrl: 'https://lobstr.co',
    isDetected: detected,
    connect: async () => {
      if (!window.lobstr) throw new Error('LOBSTR is not installed')
      return window.lobstr.getPublicKey()
    },
    getEIP1193Provider: () => null,
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Detect all wallet providers and return the full list (detected + install-able). */
export function detectAllProviders(): WalletProvider[] {
  return [
    makeEvmProvider('metamask', 'MetaMask', '🦊', 'https://metamask.io', detectMetaMask()),
    makeEvmProvider(
      'coinbase',
      'Coinbase Wallet',
      '🔵',
      'https://wallet.coinbase.com',
      detectCoinbase()
    ),
    makeEvmProvider('rabby', 'Rabby', '🐰', 'https://rabby.io', detectRabby()),
    makeEvmProvider('brave', 'Brave Wallet', '🦁', undefined, detectBrave()),
    makeEvmProvider('generic-evm', 'Browser Wallet', '🔷', undefined, detectGenericEvm()),
    makeFreighter(),
    makeLobstr(),
  ]
}

/** Look up a provider by ID. Returns null when the provider is not detected. */
export function getProviderById(id: ProviderId): WalletProvider | null {
  return detectAllProviders().find((p) => p.id === id) ?? null
}
